// How a complaint's state, severity, outcome and history read on screen. The labels for categories
// come from the server, so the patient's form and this list can never offer different words; these
// are the fallbacks for a category the server stops sending.

export const STATUS = {
  NEW:                  { label: 'New',              chip: 'bg-red-100 text-red-800' },
  UNDER_REVIEW:         { label: 'Under review',     chip: 'bg-amber-100 text-amber-800' },
  // Waiting on the complainant, not on us. Its own state because the response clock stops here.
  AWAITING_COMPLAINANT: { label: 'Waiting on them',  chip: 'bg-sky-100 text-sky-800' },
  RESOLVED:             { label: 'Resolved',         chip: 'bg-emerald-100 text-emerald-800' },
  DISMISSED:            { label: 'Dismissed',        chip: 'bg-gray-100 text-gray-600' },
};

export const statusOf = (complaint) => STATUS[complaint?.status] || STATUS.NEW;

/** The states a complaint can be moved to from where it is, in the order they are offered. */
export const NEXT_STATUSES = {
  NEW: ['UNDER_REVIEW', 'DISMISSED'],
  UNDER_REVIEW: ['AWAITING_COMPLAINANT', 'RESOLVED', 'DISMISSED'],
  AWAITING_COMPLAINANT: ['UNDER_REVIEW', 'RESOLVED', 'DISMISSED'],
  RESOLVED: ['UNDER_REVIEW'],
  DISMISSED: ['UNDER_REVIEW'],
};

/** Closing a complaint needs a reason written down; picking it up or parking it does not. */
export const NEEDS_NOTE = ['RESOLVED', 'DISMISSED'];

/** Closing also needs an outcome, so the decision is reportable and not buried in prose. */
export const NEEDS_OUTCOME = ['RESOLVED', 'DISMISSED'];

export const SEVERITY = {
  LOW:    { label: 'Low',    chip: 'bg-gray-100 text-gray-700',     days: 7 },
  MEDIUM: { label: 'Medium', chip: 'bg-amber-100 text-amber-800',   days: 3 },
  HIGH:   { label: 'High',   chip: 'bg-red-100 text-red-800',       days: 1 },
};

export const OUTCOME = {
  UPHELD:           { label: 'Upheld',             hint: 'The complaint was justified' },
  PARTLY_UPHELD:    { label: 'Partly upheld',      hint: 'Some of it was justified' },
  NOT_UPHELD:       { label: 'Not upheld',         hint: 'Looked into, nothing to answer' },
  COULD_NOT_VERIFY: { label: 'Could not verify',   hint: 'Nobody could confirm what happened' },
  DUPLICATE:        { label: 'Already reported',   hint: 'The same thing as another complaint' },
};

/**
 * Which outcomes make sense for a closing status. Resolving as "not upheld" is allowed on purpose:
 * we looked into it, there was nothing to answer, and we still went back to the complainant.
 * Dismissing as "upheld" is a contradiction, so it is not offered; the server refuses it too.
 */
export const OUTCOMES_FOR = {
  RESOLVED: ['UPHELD', 'PARTLY_UPHELD', 'NOT_UPHELD'],
  DISMISSED: ['NOT_UPHELD', 'COULD_NOT_VERIFY', 'DUPLICATE'],
};

export const CHANNEL = {
  PHONE:     'Phone call',
  IN_PERSON: 'In person',
  WHATSAPP:  'WhatsApp',
  SMS:       'SMS',
};

export const CONTACT_OUTCOME = {
  REACHED:      { label: 'Spoke to them',   tone: 'ok' },
  NO_ANSWER:    { label: 'No answer',       tone: 'warn' },
  // The usual reason a complaint cannot be verified, so it is a fact on the record rather than
  // something buried in a note.
  WRONG_NUMBER: { label: 'Wrong number',    tone: 'warn' },
  REFUSED:      { label: 'Would not talk',  tone: 'warn' },
};

export const EVENT_LABEL = {
  RECEIVED:       'Received',
  ASSIGNED:       'Assigned',
  UNASSIGNED:     'Unassigned',
  CONTACT:        'Contacted',
  NOTE:           'Note',
  STATUS_CHANGED: 'Status changed',
  INFORMED:       'Complainant told',
  TRIAGED:        'Triaged',
  REOPENED:       'Reopened',
  CLOSED:         'Closed',
};

/** Past the date it should have been dealt with. Never while parked, never once closed. */
export const isOverdue = (complaint) =>
  Boolean(complaint?.overdue) ||
  Boolean(
    complaint?.dueAt &&
      ['NEW', 'UNDER_REVIEW'].includes(complaint.status) &&
      new Date(complaint.dueAt).getTime() < Date.now(),
  );

/**
 * A complaint can arrive with no words at all: a patient who would rather write on paper, in Urdu,
 * than type into a phone attaches a photograph of the page, and that page IS the complaint. Showing
 * an empty cell for one would read as a complaint with nothing in it.
 */
export const messageOf = (complaint) => (complaint?.message || '').trim();

export const isWrittenPage = (complaint) => !messageOf(complaint) && Boolean(complaint?.photoUrl);

const CATEGORY_FALLBACK = {
  STAFF_BEHAVIOUR: 'Staff behaviour',
  MEDICINE: 'Medicine',
  CLEANLINESS: 'Cleanliness',
  WAITING_TIME: 'Waiting time',
  BILLING: 'Billing',
  OTHER: 'Other',
};

export const categoryLabel = (complaint) =>
  complaint?.categoryLabel || CATEGORY_FALLBACK[complaint?.category] || complaint?.category || '';

export const CATEGORY_KEYS = Object.keys(CATEGORY_FALLBACK);

/** Options for the shared Select, from one of the maps above. */
export const optionsFrom = (map, keys) =>
  (keys || Object.keys(map)).map((key) => ({
    value: key,
    label: typeof map[key] === 'string' ? map[key] : map[key].label,
  }));
